import { Injectable } from '@angular/core';
import { NgxHttpClient } from '@app/shared/ngx-http-client';
import { ApiServiceBase, FilterService } from '@dg/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { Observable, map } from 'rxjs';
import {
  Attribute,
  GetOrganizationAttributesResponse,
  OrganizationAttribute,
} from '../model/attribute.model';
import { Filter } from '@app/shared/components/filter/filter.component';
import { Facet } from '@app/shared/models/core-api.model';
import { makeFacetConfig } from '../utils';
import { RuleTypes } from '@app/business-rules/model';
import { catchAndSurfaceError } from '@app/shared/utils';

@Injectable({
  providedIn: 'root',
})
export class AttributesApiService extends ApiServiceBase {
  private i18n = this.translate.instant(['Core_Error']);
  constructor(
    http: NgxHttpClient,
    private translate: TranslateService,
    private filter: FilterService
  ) {
    super(http, translate.instant('OrgSvc_OrgError'));
  }

  public getAttributeFilters(orgId: Number): Observable<Filter[]> {
    return this.http
      .get<Facet[]>(`/organizations/${orgId}/attributes/facets`)
      .pipe(
        map((_facets) => {
          const { facets, facetConfigs } = makeFacetConfig(
            _facets,
            this.translate
          );
          return this.filter.facetsToFilters(facets, facetConfigs);
        })
      );
  }

  public getOrganizationAttributes(options: {
    orgId: number;
    take: number;
    pageNum: number;
    term: string;
    filters: any;
  }): Observable<GetOrganizationAttributesResponse> {
    const {
      orgId,
      term,
      pageNum: pageNumber,
      take: pageSize,
      filters: facets,
    } = options;
    return this.http
      .get<GetOrganizationAttributesResponse>(
        `/organizations/${orgId}/attributeDetails`,
        {
          params: {
            term,
            pageNumber,
            pageSize,
            facets: JSON.stringify(facets),
          },
        }
      )
      .pipe(
        map((response) => {
          response.attributes.forEach((attribute: OrganizationAttribute) => {
            // attributes are unique by id and source.  id will be used by the elf store to manage objects
            attribute.id = `${attribute.attributeId.toString()}${
              attribute.attributeSource
            }`;
          });
          return response;
        })
      );
  }

  public saveAttributeChanges(
    attribute: Partial<OrganizationAttribute>,
    orgId
  ): Observable<null> {
    return this.http.put(
      `/organizations/${orgId}/attributes/${attribute.attributeId}`,
      {
        attributeId: attribute.attributeId,
        attributeName: attribute.attributeName,
        attributeDataType: attribute.attributeDataType,
        attributeSource: attribute.attributeSource,
        visibilitySets: attribute.attributeFeatures
          .filter((item) => item.isVisible)
          .map((item) => item.featureName),
      }
    );
  }

  public checkTypeMismatch(
    attribute: Partial<OrganizationAttribute>,
    orgId
  ): Observable<{
    invalidValueCount?: number;
    validationWarning: string;
  }> {
    return this.http.post(
      `/organizations/${orgId}/attributes/datatypevalidation`,
      {
        attributeId: attribute.attributeId,
        attributeDataType: attribute.attributeDataType,
      }
    );
  }
  public getAttributeNames(
    orgId: number,
    ruleType: RuleTypes
  ): Observable<Attribute[]> {
    return this.http
      .get(`/organizations/${orgId}/businessrules/attributes/${ruleType}`)
      .pipe(catchAndSurfaceError(this.i18n.Core_Error));
  }
}
